<template>
  <div>
    <!-- Header section with Bootstrap classes -->
    <div class="mb-4">
      <div class="d-flex align-items-center gap-3 mb-2 flex-wrap">
        <div class="d-flex align-items-center gap-2">
          <h3 class="m-0">{{ $t('innovation_radar.title') }}</h3>
          <span class="selected-year-badge">{{ selectedYear }}</span>
        </div>
        
        <div class="d-flex align-items-center gap-2 ms-auto">
          <select v-model="selectedYear" class="form-select form-select-sm year-select" @change="fetchData">
            <option v-for="y in availableYears" :key="y" :value="y">{{ y }}</option>
          </select>
          <span class="ai-badge">
            <i class="fa-solid fa-wand-magic-sparkles"></i>
            {{ $t('innovation_radar.ai_powered') }}
          </span>
        </div>
      </div>
      <div class="p-3 bg-light rounded-3 border">
        <p class="text-muted mb-0" v-html="$t('innovation_radar.description')"></p>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2">{{ $t('innovation_radar.analyzing') }}</p>
    </div>

    <!-- Error State -->
    <div v-if="error" class="alert alert-warning">
      {{ error }}
    </div>

    <!-- Chart and Legend -->
    <div v-show="!loading && !error">
      <div ref="chartRef" :style="{ height: chartHeight + 'px', width: '100%' }"></div>
      
      <!-- Static Legend using simple classes -->
      <div class="d-flex flex-wrap justify-content-center gap-4 mt-2 p-3 border-top">
        <div class="d-flex align-items-center gap-2 small text-muted">
          <span class="dot size-indicator"></span>
          <span><b>{{ $t('innovation_radar.legend_size') }}:</b> {{ $t('innovation_radar.legend_volume') }}</span>
        </div>
        
        <div class="interactive-legend-item static">
          <span class="dot color-rising"></span>
          <span>{{ $t('innovation_radar.legend_rising') }}</span>
        </div>

        <div class="interactive-legend-item static">
          <span class="dot color-new"></span>
          <span>{{ $t('innovation_radar.legend_new') }}</span>
        </div>

        <div class="interactive-legend-item static">
          <span class="dot color-stable"></span>
          <span>{{ $t('innovation_radar.legend_stable') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import apiBackend from '@/api-backend';
import Highcharts from 'highcharts';
import more from 'highcharts/highcharts-more';
import exporting from 'highcharts/modules/exporting';

more(Highcharts);
exporting(Highcharts);

export default defineComponent({
  name: 'InnovationRadarChart',
  props: {
    centerId: {
      type: String,
      required: true
    },
    year: {
      type: [String, Number],
      required: false,
      default: () => new Date().getFullYear() - 1
    }
  },
  setup(props) {
    const { t } = useI18n();
    const loading = ref(false);
    const error = ref(null);
    const radarData = ref([]);
    const chartRef = ref(null);
    const chartInstance = ref(null);
    const chartHeight = ref(800);
    
    // Lógica de años: desde el año anterior al actual hasta 2024
    const currentYear = new Date().getFullYear();
    const availableYears = ref([]);
    for (let y = currentYear - 1; y >= 2024; y--) {
      availableYears.value.push(y);
    }
    
    // Inicializar con el prop year o el primer año disponible
    const selectedYear = ref(props.year || availableYears.value[0]);

    const fetchData = async () => {
      if (!props.centerId) return;
      loading.value = true;
      error.value = null;
      try {
        // Usar el año seleccionado localmente
        const yearToUse = selectedYear.value;
        
        // Llamar al endpoint del backend usando apiBackend que tiene el baseURL configurado
        const response = await apiBackend.get('/radar-ia/get', {
          params: {
            center: props.centerId,
            year: yearToUse.toString()
          }
        });
        
        // El backend devuelve el JSON directamente como string, necesitamos parsearlo
        const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        // Mostrar todos los conceptos sin filtrar
        radarData.value = data;
        
        // Dinamizamos la altura: mínimo 800px, y subimos proporcionalmente al número de burbujas
        // para que el gráfico pueda crecer hacia abajo si hay mucho contenido.
        chartHeight.value = Math.max(800, 400 + (data.length * 22));
      } catch (e) {
        radarData.value = []; // Limpiar datos en caso de error
        error.value = `Error loading trends: ${e.response?.data?.error || e.message || 'No data available'}`;
        console.error("Radar Fetch Error:", e);
      } finally {
        loading.value = false;
        await nextTick();
        renderChart();
      }
    };

    const renderChart = () => {
      if (!chartRef.value || radarData.value.length === 0) {
        // Si no hay datos, pero hay instancia, la limpiamos
        if (chartInstance.value) {
          chartInstance.value.destroy();
          chartInstance.value = null;
        }
        return;
      }

      // Si el grafico existe pero el contenedor de Highcharts no coincide (por reactividad de Vue)
      // o si la instancia se destruyó, lo creamos de nuevo.
      if (!chartInstance.value || chartInstance.value.container.parentElement !== chartRef.value) {
        if (chartInstance.value) chartInstance.value.destroy();
        chartInstance.value = Highcharts.chart(chartRef.value, chartOptions.value);
      } else {
        // Actualización eficiente
        chartInstance.value.update(chartOptions.value, true, true);
      }
    };

    onMounted(() => {
      fetchData();
    });

    onUnmounted(() => {
      if (chartInstance.value) {
        chartInstance.value.destroy();
        chartInstance.value = null;
      }
    });
    
    watch(() => props.centerId, () => {
      fetchData();
    });

    watch(() => props.year, (newYear) => {
      if (newYear) {
        selectedYear.value = newYear;
        fetchData();
      }
    });


    const chartOptions = computed(() => {
      const filteredData = radarData.value
        .map(item => ({
          name: item.tag,
          value: item.count,
          trendDisplay: item.trend === 'rising' ? t('innovation_radar.legend_rising') : (item.trend === 'new' ? t('innovation_radar.legend_new') : t('innovation_radar.legend_stable')),
          color: item.trend === 'rising' ? '#FF4757' : (item.trend === 'new' ? '#2ED573' : '#1E90FF')
        }));

      return {
        chart: {
          type: 'packedbubble',
          height: chartHeight.value + 'px',
          backgroundColor: 'transparent',
          spacing: [10, 0, 10, 0],
          animation: false
        },
        title: { text: '' },
        subtitle: { text: '' },
        tooltip: {
          useHTML: true,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 8,
          borderWidth: 1,
          borderColor: '#ddd',
          pointFormat: `
            <div style="padding: 10px; min-width: 150px">
              <span style="color:{point.color}; font-size: 18px; font-weight: bold">{point.name}</span><br/>
              <div style="margin-top: 5px; font-size: 14px; color: #666">
                <b>${t('innovation_radar.tooltip_presence')}:</b> {point.value} ${t('innovation_radar.legend_volume').toLowerCase()}<br/>
                <b>${t('innovation_radar.tooltip_trend')}:</b> <span style="text-transform: capitalize">{point.trendDisplay}</span>
              </div>
            </div>
          `
        },
        plotOptions: {
          packedbubble: {
            minSize: '40%',
            maxSize: '180%', // Reducimos un poco el máximo para evitar solapamientos excesivos
            layoutAlgorithm: {
              gravitationalConstant: 0.01,
              splitSeries: false,
              seriesInteraction: true,
              dragBetweenSeries: true,
              parentNodeLimit: true,
              bubblePadding: 12,
              enableSimulation: true, // Mantenemos la simulación pero limitamos su impacto
              initialAnimation: false
            },
            animation: false, // Desactivamos animación de la serie
            dataLabels: {
              enabled: true,
              format: '{point.name}',
              allowOverlap: false, // Desactivamos solapamiento de etiquetas para limpiar la vista
              crop: false,
              style: {
                color: '#ffffff',
                textOutline: '2px rgba(0,0,0,0.6)',
                fontWeight: '600',
                fontSize: '12px'
              }
            }
          }
        },
        legend: { enabled: false },
        series: [{
          name: t('innovation_radar.series_name'),
          data: filteredData
        }],
        exporting: { enabled: false },
        credits: { enabled: false }
      };
    });

    return {
      loading,
      error,
      chartRef,
      chartOptions,
      chartHeight,
      selectedYear,
      availableYears,
      fetchData
    };
  }
});
</script>

<style scoped>
.ai-badge {
  background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
  color: white;
  padding: 4px 14px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 10px rgba(99, 102, 241, 0.2);
}

.selected-year-badge {
  background-color: #e0f2fe;
  color: #0369a1;
  padding: 2px 10px;
  border-radius: 6px;
  font-size: 1.1rem;
  font-weight: 700;
  border: 1px solid #bae6fd;
}

.year-select {
  width: auto;
  min-width: 100px;
  font-weight: 600;
  border-color: #e2e8f0;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.dot.size-indicator { border: 1px solid #999; }
.dot.color-rising { background: #FF4757; }
.dot.color-new { background: #2ED573; }
.dot.color-stable { background: #1E90FF; }

.interactive-legend-item.static {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  user-select: none;
}

.radar-chart-element {
  width: 100%;
  border-radius: 16px;
  /* El min-height se gestiona vía Style binding si fuera necesario, 
     pero aquí aseguramos que tenga espacio base */
  min-height: 800px;
}

.loading-state, .error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 800px;
  text-align: center;
  color: #666;
  font-family: 'Outfit', sans-serif;
}

.loading-state .spinner-border {
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 25px;
  color: #6366f1 !important;
}

.chart-container {
  display: flex;
  flex-direction: column;
  min-height: 800px;
}
</style>
